import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { IPromise, IQService, isArray, isDefined, isFunction, isObject } from 'angular';
import { i18n } from 'i18next';

import { COMETD_DOMAIN_STATUS, IMetricDeprecatedService, MessagingService, None, Option, uuidToKey } from '@contact/upgrade';

import { Domain } from './models/domain';
import {
  Available,
  AvgProcessTime,
  Busy,
  ContactAbandoned,
  ContactDissuaded,
  ContactHandled,
  ContactOffered,
  DissuadedRate,
  GaveUpRate,
  Indicator,
  Pause,
  ProcessRate,
  State,
  Task,
  Waiting,
} from './models/indicator';

const MODULE_NAME = 'hypervision';
export type DomainResult = { list: Domain[]; tree: Domain };

const metricNames = [
  // agent metric names
  'SUM_CSR_AVAILABLE_DOMAIN_NOW',
  'SUM_CSR_WORKING_DOMAIN_NOW',
  'SUM_CSR_NOT_WORKING_DOMAIN_NOW',
  'SUM_CSR_TASK_DOMAIN_NOW',
  // contact metric names
  'SUM_CTT_WAITING_DOMAIN_NOW',
  'SUM_CTT_CREATED_DOMAIN',
  'SUM_CTT_SUCCESS_DOMAIN',
  'SUM_CTT_GAVE_UP_DOMAIN',
  'SUM_CTT_DISSUADED_DOMAIN',
  'TS_CTT_CALL_DURATION_DOMAIN',
];

export interface IHypervisionService {
  getDomainList(): IPromise<Domain[]>;
  isRoot(domain): boolean;
  getIndicators(syntheticView): Indicator[];
  metricCallBack(metricValues): void;
  getDomainsMetrics(): IPromise<DomainResult>;
  getDomainMetrics(domainId): Option<Domain>;
  reset(): void;
  onUpdated(callback): void;
}

export class HypervisionService implements IHypervisionService {
  static $inject: Array<string> = ['$q', 'httpDeprecated', 'deprecatedMetric', 'messaging', '$i18next'];

  public name = MODULE_NAME;

  public domainsMetrics: { [key: string]: Domain } | undefined;
  // root domain which aggregates all domains metrics
  private rootDomain: Domain;
  // callback functions to call when hypervision model is updated
  private callbacks: any[] = [];
  //  metrics subscription queries
  private metricQueries: any;
  private indicators: Indicator[];

  constructor(
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    public readonly metric: IMetricDeprecatedService,
    private readonly messaging: MessagingService,
    public readonly $i18next: i18n
  ) {
    this.indicators = [
      new ProcessRate(this),
      new GaveUpRate(this),
      new DissuadedRate(this),
      new Waiting(this),
      new AvgProcessTime(this),
      new State(this),
      new Available(this),
      new Busy(this),
      new Pause(this),
      new Task(this),
      new ContactOffered(this),
      new ContactHandled(this),
      new ContactAbandoned(this),
      new ContactDissuaded(this),
    ];
  }

  public getDomainList(): IPromise<Domain[]> {
    return this.httpDeprecated
      .asPromise()
      .get('protected/administrator/getAdministerDomainList')
      .then(response => response.data)
      .then(data => data.map(domain => Domain.of(domain, metricNames)));
  }

  private buildMetricQueries(domainList: Domain[]) {
    let queries: any[] = [];
    domainList.forEach(domain => {
      queries.push({
        entityUuid: domain.uuid,
        metricLabels: metricNames,
      });
    });
    return queries;
  }

  private aggregates(current: Domain, parent: Domain) {
    if (isDefined(current) && isDefined(parent)) {
      for (let metricName in parent.aggregatedMetrics) {
        parent.aggregatedMetrics[metricName] = parent.metrics[metricName] + current.aggregatedMetrics[metricName];
      }
    }
  }

  private buildData(indexedDomains: { [key: string]: Domain }): { [key: string]: Domain } {
    for (let domainKey in indexedDomains) {
      let domain: Domain = indexedDomains[domainKey];
      let parentId: string = uuidToKey(domain.parentId);
      let current: Domain = domain;
      let parent: Domain = indexedDomains[parentId];

      // default parent domain is root domain
      if (!isObject(parent) && !this.isRoot(current)) {
        parent = this.rootDomain;
      }
      // adding current domain in children list of its parent domain
      if (isObject(parent)) {
        parent.addChild(current);
      }
      // building parent ids list
      while (isDefined(parentId) && isDefined(indexedDomains[parentId])) {
        domain.addParentId(parentId);
        this.aggregates(current, indexedDomains[parentId]);
        current = indexedDomains[parentId];
        parentId = uuidToKey(indexedDomains[parentId].parentId);
      }
      domain.addParentId(this.rootDomain.id);
    }
    return indexedDomains;
  }

  private buildRootDomain(): Domain {
    return Domain.of(
      {
        open: true,
        domainUuid: 'rootDomainId',
        domainParent: '#',
        domainFullName: '#',
        domainName: 'hypervision:hypervision.synthetic.rootDomain',
      },
      metricNames
    );
  }

  isRoot(domain): boolean {
    if (isObject(this.rootDomain)) {
      return this.rootDomain.id === domain.id;
    }
    return false;
  }

  private buildDomainIndex(domainList: Domain[]): { [key: string]: Domain } {
    let result: { [key: string]: Domain } = {};

    // creating root domain
    this.rootDomain = this.buildRootDomain();
    result[this.rootDomain.id] = this.rootDomain;

    // creating Domain objects from objects contained in domainList
    domainList.forEach(domain => {
      let domainKey = uuidToKey(domain.uuid);
      result[domainKey] = domain;
    });

    return this.buildData(result);
  }

  private indexToList(index: { [key: string]: Domain } | undefined): Domain[] {
    let result: Domain[] = [];
    if (isObject(index)) {
      for (let key in index) {
        result.push(index[key]);
      }
    }
    return result;
  }

  getIndicators(syntheticView): Indicator[] {
    if (syntheticView) {
      return this.indicators.filter(current => current.availableInSyntheticView);
    }
    return this.indicators.map(current => {
      current.displayed = true;
      return current;
    });
  }

  private subscribeStatusChannel() {
    if (!this.messaging.isChannelOpened(COMETD_DOMAIN_STATUS)) {
      this.messaging
        .forRemote()
        .listen$<any>(COMETD_DOMAIN_STATUS)
        .subscribe(domainStatus => this.updateDomainState(domainStatus.domainId, domainStatus.isOpen));
    }
  }

  private updateDomainState(domainId, open) {
    if (isObject(this.domainsMetrics)) {
      let domainKey = uuidToKey(domainId);
      if (isObject(this.domainsMetrics[domainKey])) {
        this.domainsMetrics[domainKey].open = open;
      }
      this.notifyUpdated();
    }
  }

  private notifyUpdated() {
    this.callbacks.forEach(callback => {
      callback(this.toResponseObject(this.indexToList(this.domainsMetrics), this.rootDomain));
    });
  }

  metricCallBack(metricValues) {
    if (isObject(this.domainsMetrics) && isArray(metricValues)) {
      metricValues.forEach(metricValue => {
        this.updateDomainMetrics(this.domainsMetrics, metricValue);
      });
      this.notifyUpdated();
    }
  }

  private toResponseObject(list: Domain[], tree: Domain): DomainResult {
    return {
      list: list,
      tree: tree,
    };
  }

  getDomainsMetrics(): IPromise<DomainResult> {
    this.subscribeStatusChannel();
    let deferred = this.$q.defer<DomainResult>();
    let responseFunc = () => deferred.resolve(this.toResponseObject(this.indexToList(this.domainsMetrics), this.rootDomain));

    if (!isObject(this.domainsMetrics) || !this.metric.isChannelSubscribed()) {
      this.getDomainList().then((domainList: Domain[]) => {
        this.metricQueries = this.buildMetricQueries(domainList);
        this.domainsMetrics = this.buildDomainIndex(domainList);
        this.metric.subscribe(this.metricQueries, m => this.metricCallBack(m), true).then(responseFunc, responseFunc);
      }, responseFunc);
    } else {
      responseFunc();
    }
    return deferred.promise;
  }

  getDomainMetrics(domainId): Option<Domain> {
    let domainKey = uuidToKey(domainId);
    if (isObject(this.domainsMetrics) && isObject(this.domainsMetrics[domainKey])) {
      return Option.of(this.domainsMetrics[domainKey]);
    }
    return None;
  }

  reset() {
    if (isObject(this.metricQueries)) {
      this.metric.unsubscribe(this.metricQueries, m => this.metricCallBack(m));
      this.callbacks = [];
      for (let key in this.domainsMetrics) {
        delete this.domainsMetrics[key];
      }
      this.domainsMetrics = undefined;
      this.metricQueries = undefined;
    }
  }

  onUpdated(callback) {
    if (isFunction(callback) && this.callbacks.indexOf(callback) < 0) {
      this.callbacks.push(callback);
    }
  }

  private updateDomainMetrics(domainIndex, metricValue) {
    let domainKey = uuidToKey(metricValue.entityId);
    if (isDefined(domainIndex[domainKey])) {
      let domain = domainIndex[domainKey];
      let delta = 0;
      let i = 0;
      let parentId = '';
      for (let metricName in metricValue) {
        if (isDefined(domain.metrics[metricName])) {
          delta = metricValue[metricName] - domain.metrics[metricName];
          domain.metrics[metricName] = domain.metrics[metricName] + delta;
          domain.aggregatedMetrics[metricName] = domain.aggregatedMetrics[metricName] + delta;

          for (i = 0; i < domain.getParentIds().length; i++) {
            parentId = domain.getParentIds()[i];
            if (isDefined(domainIndex[parentId]) && isDefined(domainIndex[parentId].aggregatedMetrics[metricName])) {
              domainIndex[parentId].aggregatedMetrics[metricName] = domainIndex[parentId].aggregatedMetrics[metricName] + delta;
            }
          }

          this.rootDomain.metrics[metricName] = this.rootDomain.metrics[metricName] + delta;
        }
      }
    }
  }
}

export default MODULE_NAME;
